import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Heart, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function Favorites() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };

        const response = await fetch(`${API_URL}/api/favorites`, {
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          setFavorites(data.favorites || []);
        }
      } catch (error) {
        // Keep console.error for production error tracking
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleFavoriteChange = (propertyId, isFavorite) => {
    if (!isFavorite) {
      setFavorites(prev => prev.filter(p => p.property_id !== propertyId));
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Heart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-slate-900 mb-2">Sign in to see favorites</h2>
            <p className="text-slate-500 mb-6">Save properties you like and access them from anywhere</p>
            <Button onClick={() => navigate('/login')} className="rounded-full">
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="favorites-page">
      <Navbar />

      <div className="pt-20 pb-12">
        <div className="rh-container">
          <div className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Saved Properties
            </h1>
            <p className="text-slate-500">
              Properties you've saved for later
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-2xl overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-16">
              <Heart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="font-serif text-xl text-slate-900 mb-2">No saved properties yet</h3>
              <p className="text-slate-500 mb-6">
                Explore properties and save the ones you like by clicking the heart icon
              </p>
              <Button 
                onClick={() => navigate('/search')} 
                className="bg-accent hover:bg-accent/90 rounded-full"
              >
                <Search className="w-4 h-4 mr-2" />
                Explore Properties
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {favorites.map((property) => (
                <PropertyCard
                  key={property.property_id}
                  property={{ ...property, is_favorite: true }}
                  onFavoriteChange={(isFav) => handleFavoriteChange(property.property_id, isFav)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
